import { MIN_SHORT_EDGE, tooSmallForPainting } from "../../shared/keys.ts";

/**
 * Das Vorschaubild im Browser (ARCHITEKTUR.md 4.4, 6.4).
 *
 * Zwei Fassungen je Bild, und beide entstehen **vor** dem Hochladen: das Original geht
 * unverändert zu Anna als Arbeitsvorlage, die kleine Fassung trägt die Anordnungsfläche.
 * Der Server rechnet an keinem Bild — kein `sharp`, keine Bildverarbeitung auf dem
 * Anwendungsserver (4.1).
 *
 * Übernommen aus `momentchen/src/thumbnail.ts`, **ohne die Videowege** und mit einer
 * bewusst anderen Entscheidung im Fehlerfall — siehe `VorschauFehler`.
 */

/**
 * Lange Kante des Vorschaubildes.
 *
 * ⚠️ **900 px seit dem 24.09.2026 — vorher 400, wie in `momentchen`** (Carolins
 * Entscheidung). Dort trägt die Zahl eine Galerie-Kachel, hier wird auf demselben Bild
 * umfahren und angeordnet, und es füllt dabei den Bildschirm. Carolin hat den Prototyp auf
 * ihrem Telefon bedient und berichtet „sehr, sehr verpixelt"; die Messungen vom 24.09.
 * hatten das nicht gezeigt, weil sie alle bei Pixelverhältnis 1 liefen (die
 * Bildschirmfotos in `.playwright-mcp/` sind 390 × 844 groß).
 *
 * Gerechnet, nicht gemessen — ein hochkantes Foto auf einer Zeichenfläche von gemessenen
 * 390 × 521 CSS-px:
 *
 * | lange Kante | Vorschau | Vergrößerung bei Verhältnis 2 | bei 3 |
 * |---|---|---|---|
 * | 400 | 300 × 400 | 2,6-fach | 3,9-fach |
 * | **900** | 675 × 900 | **1,2-fach** | **1,7-fach** |
 *
 * **Was die Zahl nach oben begrenzt, ist nicht der Speicherplatz, sondern die
 * Rahmenvorschau:** dort hängen 60 Bilder gleichzeitig in der Seite (Schritt 7). Ein
 * dekodiertes JPEG kostet Breite × Höhe × 4 Byte, bei 900 px und 3 : 4 also 60 × 675 ×
 * 900 × 4 ≈ **146 MB**; bei 1600 px wären es 460 MB. Ab welcher Zahl ein Telefon den Tab
 * beendet, **weiß ich nicht** — deshalb ist 900 der Schritt, den Carolin am Gerät prüft,
 * und nicht 1600. Bricht es, ist der Weg zwei Vorschau-Größen: klein für die Reihe, groß
 * fürs Umfahren (`TODO.md`, Weg b).
 *
 * *Nebenwirkung, die auffällt:* die kurze Kante ist jetzt 675 px und liegt damit über
 * `MIN_SHORT_EDGE` (400). Bis hierher unterschritt **jedes** Vorschaubild die Schwelle,
 * ab der das Produkt selbst „wird unscharf" sagt.
 */
const LANGE_KANTE = 900;

/**
 * JPEG-Qualität. 0,8 seit dem 24.09.2026, vorher 0,7 aus `momentchen`.
 *
 * Der Grund ist derselbe: bei 3,9-facher Vergrößerung ist jeder 8 × 8-Block eines JPEG
 * 31 px groß, und die Artefakte wachsen mit. Wenn das Bild schon größer wird, soll nicht
 * die Kompression übrig bleiben. ⚠️ Die Dateigröße dazu — grob 150 KB je Bild statt 30 —
 * ist **geschätzt und nicht gemessen**; gegen das Original, das ohnehin unverändert
 * hochgeht, fällt sie ohnehin nicht auf.
 */
const QUALITAET = 0.8;

export interface Vorschau {
  blob: Blob;
  /** Maße des **Originals**, wie der Browser es dekodiert hat. */
  breite: number;
  hoehe: number;
  /** Löst dieses Bild den Auflösungshinweis aus (`MIN_SHORT_EDGE`)? */
  zuKlein: boolean;
}

/**
 * Der Fall, in dem der Browser das Bild nicht dekodieren kann — in der Praxis HEIC
 * außerhalb von Safari.
 *
 * ⚠️ **Hier entscheidet dieses Produkt anders als `momentchen`, mit Absicht.** Dort
 * bekommt ein solches Bild einen gezeichneten Platzhalter und die Galerie zeigt ein
 * dunkles Quadrat: unschön, aber das Foto ist gerettet. Hier ist die Vorschau **das
 * Werkzeug** — auf ihr wird die Person umfahren und in der Reihe angeordnet. Ein
 * Platzhalter wäre eine Kachel, die sich nicht anordnen lässt, und die Kundschaft
 * merkte es erst beim Umfahren.
 *
 * Also: früh und deutlich abweisen, mit einem Satz, der sagt, was zu tun ist.
 */
export class VorschauFehler extends Error {
  constructor() {
    super(
      "Dieses Foto kann dein Browser nicht anzeigen. Stell in den Kamera-Einstellungen " +
        "„Maximale Kompatibilität“ ein oder lade das Foto als JPEG hoch.",
    );
    this.name = "VorschauFehler";
  }
}

function kleinereMasse(ob: number, oh: number): { b: number; h: number } {
  const faktor = Math.min(1, LANGE_KANTE / Math.max(ob, oh));
  return { b: Math.max(1, Math.round(ob * faktor)), h: Math.max(1, Math.round(oh * faktor)) };
}

function alsJpeg(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new VorschauFehler());
      },
      "image/jpeg",
      QUALITAET,
    );
  });
}

/**
 * Die kleine Fassung eines Fotos — und nebenbei seine echten Maße.
 *
 * `imageOrientation: "from-image"` ist nicht optional: ohne das liegt ein hochkant
 * aufgenommenes iPhone-Foto quer, weil die Drehung nur im EXIF steht. Und das EXIF ist
 * genau das, was die Zeichenfläche beim Neukodieren wegwirft — die Vorschau trüge die
 * Drehung also nie nach.
 *
 * *Nebenwirkung, die hier erwünscht ist:* ein neu kodiertes JPEG von einer
 * Zeichenfläche hat **keine** Metadaten. Das Vorschaubild ist damit ohne weiteres Zutun
 * frei von Ortsdaten; das Original braucht dafür die Byte-Flicken aus `src/lib/gps.ts`.
 */
export async function macheVorschau(datei: Blob): Promise<Vorschau> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(datei, { imageOrientation: "from-image" });
  } catch {
    throw new VorschauFehler();
  }

  const ob = bitmap.width;
  const oh = bitmap.height;
  const { b, h } = kleinereMasse(ob, oh);

  const canvas = document.createElement("canvas");
  canvas.width = b;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    bitmap.close();
    throw new VorschauFehler();
  }
  ctx.drawImage(bitmap, 0, 0, b, h);
  bitmap.close();

  return {
    blob: await alsJpeg(canvas),
    breite: ob,
    hoehe: oh,
    zuKlein: tooSmallForPainting(ob, oh),
  };
}

/**
 * Der Satz zum Auflösungshinweis (ARCHITEKTUR.md 6.4).
 *
 * Er steht hier und nicht in der Oberfläche, damit ihn ein Test lesen kann, ohne eine
 * Komponente zu rendern — und damit er an **einer** Stelle steht, wenn Anna ihn
 * umformuliert haben will.
 */
export const HINWEIS_ZU_KLEIN =
  "Dieses Bild ist klein — die Person wird unscharf. Wenn du ein größeres hast, nimm lieber das.";

/**
 * Derselbe Hinweis in kurz — heute die Fassung für Bildschirmleser.
 *
 * ⚠️ **Geschrieben für eine 110-px-Kachel, und die gibt es seit dem 24.09.2026 nicht
 * mehr:** aus dem Raster ist eine Reihe geworden, in der eine Figur im Mittel 47 px breit
 * ist. Sichtbar passt dort kein Satz, sondern nur ein Zeichen (`src/Reihe.tsx`) — dieser
 * Text hängt deshalb im `aria-label` der Kachel, damit ein Bildschirmleser die Auskunft
 * bekommt, die sehend das gelbe Zeichen gibt. Der ganze Satz steht auf „Bild bearbeiten".
 *
 * **Kein `title`-Attribut:** ein Tooltip erscheint nur beim Überfahren mit einer Maus. Auf
 * einem Telefon gäbe es die Erklärung damit gar nicht.
 */
export const KURZ_ZU_KLEIN = "klein – wird unscharf";

/** Was unter dem Auswahlknopf steht. Aus `SPEC.md`, über `ARCHITEKTUR.md` 6.4. */
export const HINWEIS_BIS_ZU_DEN_KNIEN =
  "Wichtig: Die Person muss mindestens bis zu den Knien zu sehen sein.";

/** Für den Test und für eine spätere Erklärung in der Oberfläche. */
export const MINDESTKANTE = MIN_SHORT_EDGE;
